"use client";

import {
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  GripVertical,
} from "lucide-react";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { move } from "@dnd-kit/helpers";
import { useMemo } from "react";
import { Modal } from "modules/common/components/Modal/Modal";
import { useFacetDefinitions } from "./facetPresentation";
import type { FacetContext, GameId } from "modules/common/model/models";
import { GameIcon } from "modules/common/components/Icons/GameIcon";

interface FacetSettingsModalProps {
  open: boolean;
  onClose: () => void;
  order: string[];
  hidden: string[];
  onMoveUp: (key: string) => void;
  onMoveDown: (key: string) => void;
  onToggleHidden: (key: string) => void;
  onReorder: (newOrder: string[]) => void;
  onReset: () => void;
  context?: FacetContext;
}

export function FacetSettingsModal({
  open,
  onClose,
  order,
  hidden,
  onMoveUp,
  onMoveDown,
  onToggleHidden,
  onReorder,
  onReset,
  context = "collection",
}: FacetSettingsModalProps) {
  const hiddenSet = new Set(hidden);
  const definitions = useFacetDefinitions(context);
  const definitionMap = useMemo(
    () =>
      new Map(definitions.map((definition) => [definition.key, definition])),
    [definitions]
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Filter Settings"
      height="auto"
      maxWidth="sm"
      actions={
        <>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onReset}
          >
            Reset to Defaults
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={onClose}
          >
            Done
          </button>
        </>
      }
    >
      <p className="text-base-content/60 mb-3 text-xs">
        Drag to reorder, or use arrows. Hidden groups won&apos;t appear in the
        sidebar.
      </p>
      <DragDropProvider
        onDragEnd={(event) => {
          const reordered = move(order, event);
          if (reordered) onReorder(reordered);
        }}
      >
        <ul className="flex flex-col gap-0.5">
          {order.map((key, index) => {
            const def = definitionMap.get(key);
            if (!def) return null;

            return (
              <SortableRow
                key={key}
                facetKey={key}
                index={index}
                title={def.title}
                gameExclusive={def.gameExclusive}
                isHidden={hiddenSet.has(key)}
                isFirst={index === 0}
                isLast={index === order.length - 1}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
                onToggleHidden={onToggleHidden}
              />
            );
          })}
        </ul>
      </DragDropProvider>
    </Modal>
  );
}

interface SortableRowProps {
  facetKey: string;
  index: number;
  title: string;
  gameExclusive?: GameId;
  isHidden: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: (key: string) => void;
  onMoveDown: (key: string) => void;
  onToggleHidden: (key: string) => void;
}

function SortableRow({
  facetKey,
  index,
  title,
  gameExclusive,
  isHidden,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onToggleHidden,
}: SortableRowProps) {
  const { ref, handleRef, isDragging } = useSortable({
    id: facetKey,
    index,
  });

  return (
    <li
      ref={ref}
      className={`bg-base-100 flex items-center gap-1 rounded-lg px-1 py-1.5 transition-opacity ${
        isHidden ? "opacity-50" : ""
      } ${isDragging ? "z-50 shadow-lg" : ""}`}
    >
      <button
        ref={handleRef}
        type="button"
        className="btn btn-ghost btn-xs btn-square cursor-grab touch-none active:cursor-grabbing"
        aria-label={`Drag to reorder ${title}`}
      >
        <GripVertical size={14} className="text-base-content/40" />
      </button>

      <span className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium">
        {title}
        {gameExclusive && (
          <span className="text-base-content/40 flex items-center">
            <GameIcon game={gameExclusive} />
          </span>
        )}
      </span>

      <button
        type="button"
        className="btn btn-ghost btn-xs btn-square"
        onClick={() => onToggleHidden(facetKey)}
        aria-label={isHidden ? `Show ${title}` : `Hide ${title}`}
      >
        {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>

      <button
        type="button"
        className="btn btn-ghost btn-xs btn-square"
        onClick={() => onMoveUp(facetKey)}
        disabled={isFirst}
        aria-label={`Move ${title} up`}
      >
        <ChevronUp size={14} />
      </button>

      <button
        type="button"
        className="btn btn-ghost btn-xs btn-square"
        onClick={() => onMoveDown(facetKey)}
        disabled={isLast}
        aria-label={`Move ${title} down`}
      >
        <ChevronDown size={14} />
      </button>
    </li>
  );
}
